import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './saude.css';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/header';
import Titulo from '../../components/Titulo/titulo';
import SearchInternal from '../../components/SearchInternal/SearchInternal';
import PageHeader from '../../components/PageHeader';
import { useInternals } from '../../context/internalContext';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useUsers } from '../../context/usersContext';
import useSubmitLock from '../../utils/useSubmitLock';
import BinaryImage from '../../components/BinaryImage';
import BinaryFileLink from '../../components/BinaryFileLink';
import { BINARY_RESOURCES, hasBinaryField, resolveThumbnailField } from '../../utils/binaryFile';

const TABS = ['Triagem', 'Fotos de Lesões', 'Tratamento', 'Contatos', 'Desfecho'];

const OUTCOME_TYPES = [
  { code: 'cure', name: 'Cura (Alta por completar tratamento)' },
  { code: 'treatment_abandonment', name: 'Abandono de Tratamento' },
  { code: 'transfer', name: 'Transferência para outra Unidade' },
  { code: 'death', name: 'Óbito' },
  { code: 'diagnosis_error', name: 'Erro de Diagnóstico (Exclusão)' },
];

const DISABILITY_GRADES = [
  { code: 'grade_0', name: 'Grau 0' },
  { code: 'grade_1', name: 'Grau 1' },
  { code: 'grade_3', name: 'Grau 3' },
  { code: 'not_evaluated', name: 'Não avaliado' },
];

const SINAN_HANSENIASE_URL = 'https://portalsinan.saude.gov.br/images/documentos/Agravos/Hanseniase/Hanseniase_v5.pdf';
const ANS_HANSENIASE_URL = 'https://www.gov.br/saude/pt-br/assuntos/saude-de-a-a-z/h/hanseniase/publicacoes/formulario-para-avaliacao-neurologica-simplificada-e-classificacao-do-grau-de-incapacidade-fisica-em-hanseniase';

const formatMonthInput = (value) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 2) return digits;
  return `${digits.slice(0, 2)}/${digits.slice(2, 6)}`;
};

const Hanseniase = () => {
  const location = useLocation();
  const { internalId } = location.state || {};
  const {
    listInternals,
    listTuberculosisQuestions,
    listHanseniase,
    createHanseniase,
    updateHanseniase,
  } = useInternals();
  const { userInfo, buildUnitReadParams } = useUsers();
  const { isSubmitting, runWithSubmitLock } = useSubmitLock();

  const [internalDetails, setInternalDetails] = useState(null);
  const [hanseniaseId, setHanseniaseId] = useState(null);
  // Garante que o carregamento inicial ocorre apenas uma vez por interno selecionado
  const loadedForRef = useRef(null);
  const [activeTab, setActiveTab] = useState(0);
  const [maxUnlockedTab, setMaxUnlockedTab] = useState(0);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  // Controle de preenchimento e edição por etapa
  const [tabsFilled, setTabsFilled] = useState(new Set());
  const [dirtyTabs, setDirtyTabs] = useState(new Set());

  // Tab 0: Triagem
  const [questions, setQuestions] = useState([]);
  const [triagem, setTriagem] = useState({});
  const [observation, setObservation] = useState('');

  // Tab 1: Fotos de Lesões
  const [lesionPhotos, setLesionPhotos] = useState([]);
  const [newPhotos, setNewPhotos] = useState([]);

  // Tab 2: Tratamento
  const [followUpForm, setFollowUpForm] = useState({
    month: '', supervised_dose: '', self_administered_dose: '', expected_date: '', observation: '',
  });
  const [followUps, setFollowUps] = useState([]);

  // Tab 3: Contatos
  const [contacts, setContacts] = useState({ registered_contacts: '', contacts_examined: '' });

  // Tab 4: Desfecho
  const [desfecho, setDesfecho] = useState({ outcome_type: '', outcome_date: '', degree_disability: '' });
  const [ansFile, setAnsFile] = useState(null);
  const [sinanFile, setSinanFile] = useState(null);
  // id da triagem que já tem ANS/SINAN enviados (o arquivo sai pela rota binary da triagem)
  const [loadedAnsFileId, setLoadedAnsFileId] = useState(null);
  const [loadedSinanFileId, setLoadedSinanFileId] = useState(null);
  const [abandonmentProtocol, setAbandonmentProtocol] = useState({
    active_search_performed: false,
    reported_to_surveillance: false,
  });

  // ─── derived ──────────────────────────────────────────────────────────────

  const allTriagemAnswered = useMemo(
    () => questions.length > 0 && questions.every((q) => triagem[q.id] === true || triagem[q.id] === false),
    [triagem, questions],
  );

  const hasRisk = useMemo(
    () => Object.values(triagem).some((v) => v === true),
    [triagem],
  );

  const isAbandonmentType = useMemo(
    () => desfecho.outcome_type === 'treatment_abandonment',
    [desfecho.outcome_type],
  );

  // ─── effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    const fetchQuestions = async () => {
      setLoadingQuestions(true);
      try {
        const resp = await listTuberculosisQuestions();
        if (cancelled) return;
        const list = resp?.results || resp || [];
        setQuestions(
          list.filter((q) => {
            const sub = String(q?.section_detail?.subname || '').toLowerCase();
            return sub.includes('hanseniase') || sub.includes('hanseníase');
          }),
        );
      } catch (err) {
        if (!cancelled) {
          console.error('Erro ao carregar questões de hanseníase:', err);
          setQuestions([]);
        }
      } finally {
        if (!cancelled) setLoadingQuestions(false);
      }
    };
    fetchQuestions();
    return () => { cancelled = true; };
  // Executa uma vez ao montar — a referência da função não deve re-disparar o efeito
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!internalId) return;
    (async () => {
      try {
        const response = await listInternals(buildUnitReadParams({ id: internalId }));
        const internal = response?.results?.[0];
        if (internal) setInternalDetails(internal);
      } catch (err) {
        console.error('Erro ao carregar interno:', err);
      }
    })();
  }, [internalId, listInternals, buildUnitReadParams]);

  useEffect(() => {
    if (!internalDetails?.id) return;
    if (loadedForRef.current === internalDetails.id) return;
    loadedForRef.current = internalDetails.id;
    let cancelled = false;
    (async () => {
      try {
        const resp = await listHanseniase({ internal: internalDetails.id });
        if (cancelled) return;
        const latest = resp?.results?.[0];
        if (!latest?.id) return;
        setHanseniaseId(latest.id);

        let maxTab = 0;
        const filledTabs = new Set();

        if ((latest.answers_detail || []).length > 0) {
          maxTab = 1;
          filledTabs.add(0);
        }
        if ((latest.lesion_photos || []).length > 0) {
          filledTabs.add(1);
        }
        if ((latest.treatment_follow_ups || []).length > 0) {
          maxTab = Math.max(maxTab, 2);
          filledTabs.add(2);
        }
        if (latest.registered_contacts != null) {
          maxTab = Math.max(maxTab, 3);
          filledTabs.add(3);
        }
        if (latest.outcome_type) {
          maxTab = Math.max(maxTab, 4);
          filledTabs.add(4);
        }
        setMaxUnlockedTab(Math.min(maxTab, TABS.length - 1));
        setTabsFilled(filledTabs);

        const nextTriagem = {};
        (latest.answers_detail || []).forEach((a) => {
          const qId = a.question ?? a.question_id ?? a.id;
          const text = String(a.response || a.response_text || '').toLowerCase();
          nextTriagem[qId] = text === 'sim' || text === 'true';
        });
        setTriagem(nextTriagem);
        setObservation(latest.observation || '');

        setLesionPhotos(latest.lesion_photos || []);

        if (latest.treatment_follow_ups?.length) {
          setFollowUps(latest.treatment_follow_ups.map((f) => ({
            month: f.month,
            supervised_dose: String(f.supervised_dose ?? ''),
            self_administered_dose: String(f.self_administered_dose ?? ''),
            expected_date: f.expected_date,
            observation: f.observation || '',
          })));
        }

        setContacts({
          registered_contacts: latest.registered_contacts != null ? String(latest.registered_contacts) : '',
          contacts_examined: latest.contacts_examined != null ? String(latest.contacts_examined) : '',
        });

        setDesfecho({
          outcome_type: latest.outcome_type || '',
          outcome_date: latest.outcome_date || '',
          degree_disability: latest.degree_disability || '',
        });
        setLoadedAnsFileId(hasBinaryField(latest, 'ans_file') ? latest.id : null);
        setLoadedSinanFileId(hasBinaryField(latest, 'sinan_file') ? latest.id : null);

        if (latest.abandonment_protocol) {
          setAbandonmentProtocol({
            active_search_performed: latest.abandonment_protocol.active_search_performed ?? false,
            reported_to_surveillance: latest.abandonment_protocol.reported_to_surveillance ?? false,
          });
        }
      } catch (err) {
        if (!cancelled) console.error('Erro ao carregar registro de hanseníase:', err);
      }
    })();
    return () => { cancelled = true; };
  // Recarrega apenas quando o interno muda — referência de listHanseniase não deve re-disparar
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalDetails?.id]);

  // ─── dirty tracking ────────────────────────────────────────────────────────

  const markDirty = useCallback(() => {
    setDirtyTabs((prev) => new Set([...prev, activeTab]));
  }, [activeTab]);

  // ─── payload builder ───────────────────────────────────────────────────────
  // Retorna FormData APENAS quando há upload de arquivo binário (Tab 1 com fotos,
  // Tab 4 com PDFs). Para os demais tabs retorna objeto JSON puro, pois com
  // multipart/form-data o backend interpreta campos ausentes como vazios e apaga
  // lesion_photos, answers, etc. Com JSON, campo ausente = "não mexa neste campo".

  const buildTabData = useCallback((tab) => {
    const currentAnswers = questions
      .filter((q) => triagem[q.id] === true || triagem[q.id] === false)
      .map((q) => ({ question_id: q.id, response_text: triagem[q.id] ? 'Sim' : 'Não' }));

    if (tab === 0) {
      const payload = {
        internal: internalDetails?.id,
        answers: currentAnswers,
        observation: observation || null,
      };
      if (!hanseniaseId) payload.health_professional = userInfo?.id || undefined;
      return payload;
    }

    if (tab === 1) {
      // Upload binário → FormData. Envia apenas as novas fotos; o backend as adiciona às existentes.
      const fd = new FormData();
      if (currentAnswers.length) fd.append('answers', JSON.stringify(currentAnswers));
      newPhotos.forEach((photo, idx) => fd.append(`lesion_photos[${idx}][image]`, photo));
      return fd;
    }

    // Tabs 2, 3, 4: JSON puro — PATCH parcial em JSON preserva campos ausentes no backend,
    // incluindo lesion_photos. NÃO inclui lesion_photos para evitar erros de validação de arquivo.
    if (tab === 2) {
      return {
        answers: currentAnswers,
        treatment_follow_ups: followUps.map((f) => ({
          month: f.month,
          supervised_dose: Number(f.supervised_dose),
          self_administered_dose: Number(f.self_administered_dose),
          expected_date: f.expected_date,
          observation: f.observation || null,
        })),
      };
    }

    if (tab === 3) {
      return {
        answers: currentAnswers,
        registered_contacts: contacts.registered_contacts !== '' ? Number(contacts.registered_contacts) : 0,
        contacts_examined: contacts.contacts_examined !== '' ? Number(contacts.contacts_examined) : 0,
      };
    }

    if (tab === 4) {
      // JSON para campos de texto; arquivos enviados em FormData separado no handleButtonClick
      const payload = {
        answers: currentAnswers,
        outcome_type: desfecho.outcome_type || null,
        outcome_date: desfecho.outcome_date || null,
        degree_disability: desfecho.degree_disability || null,
      };
      if (isAbandonmentType) payload.abandonment_protocol = abandonmentProtocol;
      return payload;
    }

    return {};
  }, [
    questions, triagem, observation, newPhotos, followUps, contacts,
    desfecho, abandonmentProtocol, isAbandonmentType, internalDetails, userInfo, hanseniaseId,
  ]);

  // ─── handlers ──────────────────────────────────────────────────────────────

  const handleInternalSelect = async (internal) => {
    loadedForRef.current = null;
    setInternalDetails(internal);
    try {
      if (!internal?.id) return;
      const response = await listInternals(buildUnitReadParams({ id: internal.id }));
      const selected = response?.results?.[0];
      if (selected) setInternalDetails(selected);
    } catch (err) {
      console.error('Erro ao carregar interno:', err);
    }
  };

  const goToTab = (tabIndex) => {
    if (tabIndex <= maxUnlockedTab) setActiveTab(tabIndex);
  };

  // Após salvar: marca tab como preenchida, remove dirty, avança uma etapa a partir da atual
  const afterSave = useCallback((savedTab, result) => {
    setTabsFilled((prev) => new Set([...prev, savedTab]));
    setDirtyTabs((prev) => { const next = new Set(prev); next.delete(savedTab); return next; });
    if (hasBinaryField(result, 'ans_file')) setLoadedAnsFileId(result.id);
    if (hasBinaryField(result, 'sinan_file')) setLoadedSinanFileId(result.id);
    if (savedTab === 1 && Array.isArray(result?.lesion_photos)) {
      setLesionPhotos(result.lesion_photos);
      setNewPhotos([]);
    }
    if (savedTab < TABS.length - 1) {
      const nextTab = savedTab + 1;
      setMaxUnlockedTab((prev) => Math.max(prev, nextTab));
      setActiveTab(nextTab);
    }
  }, []);

  const getButtonLabel = () => {
    if (isSubmitting) return 'Salvando...';
    // Tab 1 (Fotos): depende de se há novas fotos selecionadas
    if (activeTab === 1) {
      return newPhotos.length > 0 ? 'Salvar e próxima' : 'Próxima etapa';
    }
    const isFilled = tabsFilled.has(activeTab);
    const isDirty = dirtyTabs.has(activeTab);
    const isLast = activeTab === TABS.length - 1;
    if (isFilled && !isDirty) return isLast ? 'Concluído' : 'Próxima etapa';
    if (isFilled && isDirty) return isLast ? 'Atualizar e finalizar' : 'Atualizar e próxima';
    return isLast ? 'Salvar e finalizar' : 'Salvar e próxima';
  };

  const handleButtonClick = async () => {
    if (!internalDetails?.id) { toast.error('Selecione um interno para iniciar o fluxo.'); return; }

    // Tab 1 (Fotos) — sem novas fotos, apenas avança
    if (activeTab === 1 && newPhotos.length === 0) {
      const nextTab = Math.min(activeTab + 1, TABS.length - 1);
      setMaxUnlockedTab((prev) => Math.max(prev, nextTab));
      setActiveTab(nextTab);
      return;
    }

    // Etapa preenchida e sem alterações — apenas navega para a próxima
    const isFilled = tabsFilled.has(activeTab);
    const isDirty = dirtyTabs.has(activeTab);
    if (isFilled && !isDirty) {
      if (activeTab === 0 && !hasRisk) {
        toast.info('Sem suspeita de hanseníase. Todas as respostas são negativas — não é possível avançar.');
        return;
      }
      setActiveTab(Math.min(activeTab + 1, TABS.length - 1));
      return;
    }

    // Validações por etapa
    if (activeTab === 0) {
      if (!allTriagemAnswered) { toast.error('Responda todas as perguntas da triagem.'); return; }
    }
    if (activeTab === 2 && !followUps.length) {
      toast.error('Adicione ao menos um registro de acompanhamento de tratamento.'); return;
    }
    if (activeTab === 4) {
      if (!desfecho.outcome_type || !desfecho.outcome_date || !desfecho.degree_disability) {
        toast.error('Preencha tipo de desfecho, data e grau de incapacidade.'); return;
      }
    }

    await runWithSubmitLock(async () => {
      const tabData = buildTabData(activeTab);
      const isMultipart = tabData instanceof FormData;

      let result;
      if (activeTab === 0 && !hanseniaseId) {
        result = await createHanseniase(tabData, isMultipart);
        if (result?.id) setHanseniaseId(result.id);
      } else {
        result = await updateHanseniase(hanseniaseId, tabData, isMultipart);
        // Tab 4: após salvar campos de texto via JSON, enviar arquivos em FormData separado
        if (result && activeTab === 4 && (ansFile || sinanFile)) {
          const fd = new FormData();
          if (ansFile) fd.append('ans_file', ansFile);
          if (sinanFile) fd.append('sinan_file', sinanFile);
          result = await updateHanseniase(hanseniaseId, fd, true);
        }
      }
      if (!result) return;

      // Triagem sem risco: salva mas não avança
      if (activeTab === 0 && !hasRisk) {
        setTabsFilled((prev) => new Set([...prev, 0]));
        setDirtyTabs((prev) => { const next = new Set(prev); next.delete(0); return next; });
        toast.info('Triagem salva. Sem suspeita de hanseníase — todas as respostas são negativas.');
        return;
      }

      if (activeTab === TABS.length - 1) {
        toast.success('Prontuário de hanseníase finalizado com sucesso!');
      }
      afterSave(activeTab, result);
    });
  };

  const addFollowUp = () => {
    const { month, supervised_dose, self_administered_dose, expected_date } = followUpForm;
    if (!month || supervised_dose === '' || self_administered_dose === '' || !expected_date) {
      toast.error('Preencha mês, doses e data prevista.'); return;
    }
    if (!/^(0[1-9]|1[0-2])\/\d{4}$/.test(month)) {
      toast.error('Mês deve estar no formato MM/AAAA.'); return;
    }
    setFollowUps((prev) => ([...prev, { ...followUpForm }]));
    setFollowUpForm({ month: '', supervised_dose: '', self_administered_dose: '', expected_date: '', observation: '' });
    markDirty();
  };

  const removeFollowUp = (index) => {
    setFollowUps((prev) => prev.filter((_, i) => i !== index));
    markDirty();
  };

  // ─── renders ──────────────────────────────────────────────────────────────

  const renderTriagemTab = () => (
    <div className="tb-step-card">
      <h4>1. QUESTIONÁRIO DE TRIAGEM</h4>
      {loadingQuestions && <p className="health-note">Carregando perguntas...</p>}
      <div className="tb-triage-list">
        {questions.map((q) => (
          <div key={q.id} className="tb-triage-item">
            <p>{q.description}</p>
            <div className="tb-yes-no">
              <button
                type="button"
                className={triagem[q.id] === true ? 'active' : ''}
                onClick={() => { setTriagem((prev) => ({ ...prev, [q.id]: true })); markDirty(); }}
              >SIM</button>
              <button
                type="button"
                className={triagem[q.id] === false ? 'active' : ''}
                onClick={() => { setTriagem((prev) => ({ ...prev, [q.id]: false })); markDirty(); }}
              >NAO</button>
            </div>
          </div>
        ))}
      </div>
      {allTriagemAnswered && !hasRisk && (
        <div className="tb-alert-info">
          <strong>SEM SUSPEITA DE HANSENÍASE</strong>
          <span>Todas as respostas são negativas. O fluxo não prosseguirá para as próximas etapas.</span>
        </div>
      )}
      {hasRisk && (
        <div className="tb-alert-danger">
          <strong>RISCO DE HANSENÍASE DETECTADO</strong>
          <span>Paciente com sintomas positivos. Proceder com investigação clínica.</span>
        </div>
      )}
      <div className="tb-text-block" style={{ marginTop: 16 }}>
        <label>Observações</label>
        <textarea
          rows={4}
          value={observation}
          onChange={(e) => { setObservation(e.target.value); markDirty(); }}
          placeholder="Observações adicionais sobre o questionário..."
        />
      </div>
    </div>
  );

  const renderFotosTab = () => (
    <div className="tb-step-card">
      <h4>2. REGISTRO FOTOGRÁFICO DE LESÕES</h4>
      <p className="health-note">Formatos aceitos: JPG, JPEG, PNG. Esta etapa é opcional.</p>
      <div className="tb-sub-card">
        <label>Adicionar fotos de lesões</label>
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png,.jpg,.jpeg,.png"
          multiple
          onChange={(e) => setNewPhotos(Array.from(e.target.files || []))}
        />
        {newPhotos.length > 0 && (
          <p className="health-note">{newPhotos.length} foto(s) selecionada(s) para upload.</p>
        )}
      </div>
      {lesionPhotos.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <h5>Fotos registradas</h5>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
            {lesionPhotos.map((photo) => (
              <BinaryFileLink key={photo.id} resource={BINARY_RESOURCES.hanseniaseLesionPhoto} id={photo.id} field="image">
                <BinaryImage
                  resource={BINARY_RESOURCES.hanseniaseLesionPhoto}
                  id={photo.id}
                  field={resolveThumbnailField(photo, 'image')}
                  alt={`Lesão ${photo.id}`}
                  style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 4, border: '1px solid #ddd' }}
                />
              </BinaryFileLink>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const renderTratamentoTab = () => (
    <div className="tb-step-card">
      <h4>3. ACOMPANHAMENTO DE TRATAMENTO</h4>
      <div className="tb-sub-card">
        <div className="tb-two-cols">
          <div>
            <label>Mês (MM/AAAA)</label>
            <input
              type="text"
              placeholder="Ex: 03/2026"
              maxLength={7}
              value={followUpForm.month}
              onChange={(e) => setFollowUpForm((prev) => ({ ...prev, month: formatMonthInput(e.target.value) }))}
            />
          </div>
          <div>
            <label>Data prevista</label>
            <input
              type="date"
              value={followUpForm.expected_date}
              onChange={(e) => setFollowUpForm((prev) => ({ ...prev, expected_date: e.target.value }))}
            />
          </div>
        </div>
        <div className="tb-two-cols">
          <div>
            <label>Dose supervisionada</label>
            <input
              type="number"
              min="0"
              value={followUpForm.supervised_dose}
              onChange={(e) => setFollowUpForm((prev) => ({ ...prev, supervised_dose: e.target.value.replace(/[^\d]/g, '') }))}
            />
          </div>
          <div>
            <label>Dose auto-administrada</label>
            <input
              type="number"
              min="0"
              value={followUpForm.self_administered_dose}
              onChange={(e) => setFollowUpForm((prev) => ({ ...prev, self_administered_dose: e.target.value.replace(/[^\d]/g, '') }))}
            />
          </div>
        </div>
        <div>
          <label>Observação</label>
          <textarea
            rows={3}
            value={followUpForm.observation}
            onChange={(e) => setFollowUpForm((prev) => ({ ...prev, observation: e.target.value }))}
          />
        </div>
        <button type="button" className="tb-btn-small" onClick={addFollowUp}>ADICIONAR REGISTRO</button>
      </div>

      {followUps.length > 0 && (
        <div className="tb-timeline">
          {followUps.map((item, index) => (
            <div key={`${item.month}-${index}`} className="tb-timeline-item">
              <strong>{item.month}</strong>
              <p>Dose supervisionada: {item.supervised_dose} | Auto-administrada: {item.self_administered_dose}</p>
              <p>Data prevista: {item.expected_date}</p>
              {item.observation && <p>{item.observation}</p>}
              <button
                type="button"
                className="tb-sample-remove"
                onClick={() => removeFollowUp(index)}
                aria-label={`Remover registro ${index + 1}`}
              >X</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderContatosTab = () => (
    <div className="tb-step-card">
      <h4>4. EXAME DE CONTATOS</h4>
      <p className="health-note">Informe o número de contatos registrados e examinados do caso.</p>
      <div className="tb-two-cols">
        <div>
          <label>Total de contatos registrados</label>
          <input
            type="number"
            min="0"
            value={contacts.registered_contacts}
            onChange={(e) => { setContacts((prev) => ({ ...prev, registered_contacts: e.target.value.replace(/[^\d]/g, '') })); markDirty(); }}
          />
        </div>
        <div>
          <label>Total de contatos examinados</label>
          <input
            type="number"
            min="0"
            value={contacts.contacts_examined}
            onChange={(e) => { setContacts((prev) => ({ ...prev, contacts_examined: e.target.value.replace(/[^\d]/g, '') })); markDirty(); }}
          />
        </div>
      </div>
    </div>
  );

  const renderDesfechoTab = () => (
    <div className="tb-step-card">
      <h4>5. ALTA / DESFECHO</h4>
      <div className="tb-two-cols">
        <div>
          <label>Tipo de desfecho</label>
          <select
            value={desfecho.outcome_type}
            onChange={(e) => { setDesfecho((prev) => ({ ...prev, outcome_type: e.target.value })); markDirty(); }}
          >
            <option value="">Selecione...</option>
            {OUTCOME_TYPES.map((t) => (
              <option key={t.code} value={t.code}>{t.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label>Data do desfecho</label>
          <input
            type="date"
            value={desfecho.outcome_date}
            onChange={(e) => { setDesfecho((prev) => ({ ...prev, outcome_date: e.target.value })); markDirty(); }}
          />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <label>Grau de incapacidade na alta</label>
        <div className="tb-status-row">
          {DISABILITY_GRADES.map((g) => (
            <button
              key={g.code}
              type="button"
              className={desfecho.degree_disability === g.code ? 'active' : ''}
              onClick={() => { setDesfecho((prev) => ({ ...prev, degree_disability: g.code })); markDirty(); }}
            >{g.name}</button>
          ))}
        </div>
      </div>

      <div className="tb-two-cols" style={{ marginTop: 20 }}>
        <div className="tb-sinan-box">
          <div>
            <strong>AVALIAÇÃO NEUROLÓGICA SIMPLIFICADA (ANS)</strong>
            <p>Realize o download, preencha e anexe o formulário.</p>
          </div>
          <a href={ANS_HANSENIASE_URL} target="_blank" rel="noreferrer" className="tb-btn-outline">
            BAIXAR FORMULÁRIO ANS
          </a>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => { setAnsFile(e.target.files?.[0] || null); markDirty(); }}
          />
          {loadedAnsFileId && (
            <BinaryFileLink resource={BINARY_RESOURCES.hanseniase} id={loadedAnsFileId} field="ans_file" className="a-option" style={{ marginTop: 6, display: 'block' }}>
              Ver ANS enviada
            </BinaryFileLink>
          )}
        </div>
        <div className="tb-sinan-box">
          <div>
            <strong>NOTIFICAÇÃO SINAN</strong>
            <p>Realize o download, preencha e anexe a ficha SINAN.</p>
          </div>
          <a href={SINAN_HANSENIASE_URL} target="_blank" rel="noreferrer" className="tb-btn-outline">
            BAIXAR FICHA SINAN
          </a>
          <input
            type="file"
            accept="application/pdf,.pdf"
            onChange={(e) => { setSinanFile(e.target.files?.[0] || null); markDirty(); }}
          />
          {loadedSinanFileId && (
            <BinaryFileLink resource={BINARY_RESOURCES.hanseniase} id={loadedSinanFileId} field="sinan_file" className="a-option" style={{ marginTop: 6, display: 'block' }}>
              Ver SINAN enviado
            </BinaryFileLink>
          )}
        </div>
      </div>

      {isAbandonmentType && (
        <div className="tb-transfer-box" style={{ marginTop: 16 }}>
          <h5>PROTOCOLO DE ABANDONO DE TRATAMENTO</h5>
          <label className="tb-checkbox-line">
            <input
              type="checkbox"
              checked={abandonmentProtocol.active_search_performed}
              onChange={(e) => { setAbandonmentProtocol((prev) => ({ ...prev, active_search_performed: e.target.checked })); markDirty(); }}
            />
            Busca ativa realizada?
          </label>
          <label className="tb-checkbox-line" style={{ marginTop: 8 }}>
            <input
              type="checkbox"
              checked={abandonmentProtocol.reported_to_surveillance}
              onChange={(e) => { setAbandonmentProtocol((prev) => ({ ...prev, reported_to_surveillance: e.target.checked })); markDirty(); }}
            />
            Notificado à vigilância?
          </label>
        </div>
      )}
    </div>
  );

  return (
    <div>
      <Sidebar />
      <Header />
      <div className="container social-container admission-page">
        <Titulo children={<SearchInternal onSelect={handleInternalSelect} />} />
        <PageHeader
          title="Consulta de Enfermagem - Hanseníase"
          subtitle="Protocolo de rastreamento, tratamento e encerramento"
          internalDetails={internalDetails}
        />

        <div className="health-card tb-tabs-layout">
          <div className="tb-tabs-header">
            {TABS.map((label, index) => (
              <button
                key={label}
                type="button"
                className={`tb-tab-btn ${activeTab === index ? 'active' : ''} ${index > maxUnlockedTab ? 'disabled' : ''}`}
                onClick={() => goToTab(index)}
                disabled={index > maxUnlockedTab}
              >
                {index + 1}. {label}
              </button>
            ))}
          </div>

          {activeTab === 0 && renderTriagemTab()}
          {activeTab === 1 && renderFotosTab()}
          {activeTab === 2 && renderTratamentoTab()}
          {activeTab === 3 && renderContatosTab()}
          {activeTab === 4 && renderDesfechoTab()}

          <div className="tb-footer-action">
            <button
              type="button"
              className={`social-submit-button ${activeTab === TABS.length - 1 ? 'tb-finish-btn' : ''}`}
              onClick={handleButtonClick}
              disabled={isSubmitting}
            >
              {getButtonLabel()}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Hanseniase;
